import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  OutlinedInput,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
  Checkbox,
  Button,
  CircularProgress,
  Chip,
  Paper,
} from '@mui/material';
import { Info } from '@mui/icons-material';
import type { Task, Product, ProductDetail, User } from '../types';
import { apiRequest } from '../utils/api';
import { formatDateLocal } from '../utils/dateUtils';
import AddressMapModal from './AddressMapModal';

interface JobRoomOption {
  jobId: number;
  jobNumber: string;
  roomId: number | null;
  roomName: string | null;
  displayText: string;
}

interface TaskModalProps {
  open: boolean;
  editingTask: Task | null;
  initialDate?: Date;
  onClose: () => void;
  onSave: (taskData: any) => Promise<void>;
  onRefreshTasks?: () => Promise<void>;
}

export default function TaskModal({
  open,
  editingTask,
  initialDate,
  onClose,
  onSave,
}: TaskModalProps) {
  // Form state
  const [selectedTaskType, setSelectedTaskType] = useState<'Measure' | 'Shop Drawing' | 'Fabrication' | 'Install' | ''>('');
  const [selectedJobRoom, setSelectedJobRoom] = useState<string>('');
  const [taskDescription, setTaskDescription] = useState('');
  const [taskDate, setTaskDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [isMultiDay, setIsMultiDay] = useState(false);
  const [selectedTeamLeader, setSelectedTeamLeader] = useState<number | ''>('');
  const [selectedMembers, setSelectedMembers] = useState<number[]>([]);
  const [hasWorkLocation, setHasWorkLocation] = useState(false);
  const [workLocationAddress, setWorkLocationAddress] = useState('');
  const [workLocationLatitude, setWorkLocationLatitude] = useState<number | null>(null);
  const [workLocationLongitude, setWorkLocationLongitude] = useState<number | null>(null);
  const [workLocationId, setWorkLocationId] = useState<number | null>(null);
  const [openAddressMapModal, setOpenAddressMapModal] = useState(false);

  // Data state
  const [users, setUsers] = useState<User[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [jobRoomOptions, setJobRoomOptions] = useState<JobRoomOption[]>([]);
  const [roomProductDetails, setRoomProductDetails] = useState<Array<{
    detail: ProductDetail;
    product: Product;
  }>>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productDetailStatusUpdates, setProductDetailStatusUpdates] = useState<Map<number, string>>(new Map());
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);

  // Status options for product details
  const detailStatusOptions = [
    'TO CUT',
    'CUT',
    'TO POLISH',
    'POLISHED',
    'TO TEMPER',
    'TEMPERED',
    'TO LAMI',
    '@ J&A',
    '@ G&S',
    'TO BEVEL',
    'PREDRILLED',
  ];

  // Helper function to get product detail name
  const getProductDetailName = (_product: Product, detail: ProductDetail): string => {
    const parts: string[] = [];
    if (detail.width) parts.push(`W: ${detail.width}"`);
    if (detail.height) parts.push(`H: ${detail.height}"`);
    if (detail.number) parts.push(`#${detail.number}`);
    return parts.length > 0 ? parts.join(' ') : `Detail #${detail.id}`;
  };

  // Load all data when editing a task (single API call)
  useEffect(() => {
    if (!editingTask || !open || !editingTask.id) {
      return;
    }

    let isCancelled = false;

    const loadAllEditingData = async () => {
      setLoadingJobs(true);
      try {
        // Single request to get all edit data
        const response = await apiRequest(`/tasks/${editingTask.id}/edit-data`, { method: 'GET' });
        const editData = response.data;

        if (isCancelled) return;

        // Set task data (already have it from editingTask, but ensure we have latest)
        // The backend returns the full task data

        // Set job-room options
        setJobRoomOptions(editData.jobRoomOptions || []);

        // Set product details
        if (editData.products && editData.products.length > 0) {
          const productDetailsWithProducts: Array<{
            detail: ProductDetail;
            product: Product;
          }> = [];

          editData.products.forEach((product: any) => {
            if (product.details && product.details.length > 0) {
              product.details.forEach((detail: any) => {
                productDetailsWithProducts.push({
                  detail: {
                    id: detail.id,
                    product_id: detail.product_id,
                    job_id: detail.job_id,
                    product_type_id: detail.product_type_id,
                    width: detail.width,
                    height: detail.height,
                    number: detail.number,
                    status: detail.status,
                    barcode: detail.barcode,
                    created_at: detail.created_at,
                    updated_at: detail.updated_at,
                  },
                  product: {
                    id: product.id,
                    product_name: product.product_name,
                    room: product.room,
                    status: product.status,
                    note: product.note,
                    product_type_id: product.product_type_id,
                    order: product.order,
                    job_id: product.job_id,
                    details: product.details.map((d: any) => ({
                      id: d.id,
                      product_id: d.product_id,
                      job_id: d.job_id,
                      product_type_id: d.product_type_id,
                      width: d.width,
                      height: d.height,
                      number: d.number,
                      status: d.status,
                      barcode: d.barcode,
                      created_at: d.created_at,
                      updated_at: d.updated_at,
                    })),
                    created_at: product.created_at,
                    updated_at: product.updated_at,
                  },
                });
              });
            }
          });

          if (!isCancelled) {
            setRoomProductDetails(productDetailsWithProducts);

            // Restore product detail status updates from editing task
            if (editingTask.productDetailStatusUpdates) {
              const updatesMap = new Map<number, string>();
              Object.entries(editingTask.productDetailStatusUpdates).forEach(([detailId, status]) => {
                updatesMap.set(Number(detailId), status);
              });
              setProductDetailStatusUpdates(updatesMap);
            }
          }
        } else {
          setRoomProductDetails([]);
          setProductDetailStatusUpdates(new Map());
        }

        // Set work location
        if (editData.workLocation && !isCancelled) {
          setWorkLocationId(editData.workLocation.id);
          setHasWorkLocation(true);
          setWorkLocationAddress(editData.workLocation.location || '');
          setWorkLocationLatitude(editData.workLocation.latitude ? Number(editData.workLocation.latitude) : null);
          setWorkLocationLongitude(editData.workLocation.longitude ? Number(editData.workLocation.longitude) : null);
        } else if (!isCancelled) {
          setWorkLocationId(null);
          setHasWorkLocation(false);
          setWorkLocationAddress('');
          setWorkLocationLatitude(null);
          setWorkLocationLongitude(null);
        }
      } catch (error) {
        console.error('Error loading editing data:', error);
      } finally {
        if (!isCancelled) {
          setLoadingJobs(false);
        }
      }
    };

    loadAllEditingData();
    
    return () => {
      isCancelled = true;
    };
  }, [editingTask?.id, open]);

  // Only fetch when task type is selected (not when dialog opens, and not when editing)
  useEffect(() => {
    // Skip if editing (handled by separate effect above)
    if (editingTask) {
      return;
    }
    
    // Only load jobs when dialog is open AND task type is selected
    if (!open || !selectedTaskType) {
      return;
    }

    const loadUnassignedJobRooms = async () => {
      setLoadingJobs(true);
      try {
        // Fetch unassigned job-room combinations in a single request
        const response = await apiRequest(`/tasks/unassigned-job-rooms?taskType=${selectedTaskType}`, { method: 'GET' });
        const options: JobRoomOption[] = (response.data || []) as JobRoomOption[];
        
        setJobRoomOptions(options);
      } catch (error) {
        console.error('Error loading unassigned job-room combinations:', error);
        setJobRoomOptions([]);
      } finally {
        setLoadingJobs(false);
      }
    };

    loadUnassignedJobRooms();
  }, [selectedTaskType, open, editingTask]);

  // No longer needed - backend now returns only unassigned job-room combinations
  // Keeping allAssignedTasks state for backward compatibility but it's not used for new tasks

  // Load users for team leader and members selection
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const usersResponse = await apiRequest('/users', { method: 'GET' });
        const usersData = (usersResponse.data || []) as User[];
        // Filter to only approved users
        const approvedUsers = usersData.filter(user => user.approved);
        setUsers(approvedUsers);
      } catch (error) {
        console.error('Error loading users:', error);
        setUsers([]);
      }
    };

    loadUsers();
  }, []);

  // Load current user ID
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const response = await apiRequest('/users/profile/me', { method: 'GET' });
        const user = response.data as User;
        setCurrentUserId(user.id);
      } catch (error) {
        console.error('Error loading current user:', error);
      }
    };

    loadCurrentUser();
  }, []);

  // Load product details when room is selected (skip when editing - handled by editing effect)
  useEffect(() => {
    // Skip if editing (product details are loaded in the editing effect)
    if (editingTask) {
      return;
    }
    
    const loadProductDetailsForRoom = async () => {
      if (!selectedJobRoom) {
        setRoomProductDetails([]);
        setProductDetailStatusUpdates(new Map());
        return;
      }

      const [jobIdStr, roomIdStr] = selectedJobRoom.split('-');
      const jobId = parseInt(jobIdStr, 10);
      const roomId = parseInt(roomIdStr, 10);

      const selectedOption = jobRoomOptions.find(
        opt => opt.jobId === jobId && opt.roomId === roomId
      );

      if (!selectedOption) {
        setRoomProductDetails([]);
        return;
      }

      setLoadingProducts(true);
      try {
        // Fetch products for the job
        const productsResponse = await apiRequest(`/products/job/${jobId}`, { method: 'GET' });
        const productsData = (productsResponse.data || []) as Product[];
        
        // Filter products by room name
        const filteredProducts = productsData.filter(
          product => selectedOption.roomName && product.room?.toLowerCase().trim() === selectedOption.roomName.toLowerCase().trim()
        );
        
        // Flatten all product details with their parent product
        const productDetailsWithProducts: Array<{
          detail: ProductDetail;
          product: Product;
        }> = [];
        
        filteredProducts.forEach(product => {
          if (product.details && product.details.length > 0) {
            product.details.forEach(detail => {
              productDetailsWithProducts.push({
                detail,
                product,
              });
            });
          }
        });
        
        setRoomProductDetails(productDetailsWithProducts);
        setProductDetailStatusUpdates(new Map());
      } catch (error) {
        console.error('Error loading product details:', error);
        setRoomProductDetails([]);
      } finally {
        setLoadingProducts(false);
      }
    };

    loadProductDetailsForRoom();
  }, [selectedJobRoom, jobRoomOptions, editingTask]);

  // Initialize form when editing task or opening for new task
  useEffect(() => {
    if (open) {
      if (editingTask) {
        // Set form values from editing task
        if (editingTask.jobId && editingTask.roomId) {
          setSelectedJobRoom(`${editingTask.jobId}-${editingTask.roomId}`);
        } else if (editingTask.jobId) {
          setSelectedJobRoom(`${editingTask.jobId}-null`);
        } else {
          setSelectedJobRoom('');
        }
        setTaskDescription(editingTask.description || '');
        // Check if task has date range (endDate exists and is not null/empty means multi-day)
        if (editingTask.endDate && editingTask.endDate !== null && editingTask.endDate !== '') {
          setTaskDate(editingTask.taskDate || '');
          setEndDate(editingTask.endDate);
          setIsMultiDay(true);
        } else {
          setTaskDate(editingTask.taskDate || '');
          setEndDate(editingTask.taskDate || '');
          setIsMultiDay(false);
        }
        setSelectedTeamLeader(editingTask.teamLeaderId || '');
        setSelectedMembers(editingTask.memberIds || []);
        setSelectedTaskType(editingTask.taskType || '');
      } else {
        // Reset form for new task
        setSelectedJobRoom('');
        setTaskDescription('');
        setTaskDate(initialDate ? formatDateLocal(initialDate) : formatDateLocal(new Date()));
        setEndDate(initialDate ? formatDateLocal(initialDate) : formatDateLocal(new Date()));
        setIsMultiDay(false);
        setSelectedTeamLeader('');
        setSelectedMembers([]);
        setSelectedTaskType('');
        setRoomProductDetails([]);
        setProductDetailStatusUpdates(new Map());
        setHasWorkLocation(false);
        setWorkLocationAddress('2126 Flatbush Ave, Brooklyn, NY 11234');
        setWorkLocationLatitude(40.6180192);
        setWorkLocationLongitude(-73.9322130);
        setWorkLocationId(null);
      }
    }
  }, [open, editingTask, initialDate]);

  // Ensure job/room is selected when editing and options are loaded
  useEffect(() => {
    if (editingTask && editingTask.jobId && editingTask.roomId && jobRoomOptions.length > 0) {
      const jobRoomKey = `${editingTask.jobId}-${editingTask.roomId}`;
      // Only set if not already set
      if (selectedJobRoom !== jobRoomKey) {
        setSelectedJobRoom(jobRoomKey);
      }
    }
  }, [editingTask?.id, jobRoomOptions.length, selectedJobRoom]);

  // Clear job/room data when dialog closes or task type is cleared
  useEffect(() => {
    if (!open || !selectedTaskType) {
      setJobRoomOptions([]);
    }
  }, [open, selectedTaskType]);

  // Clear selected job/room when task type changes
  useEffect(() => {
    if (selectedTaskType && !editingTask) {
      setSelectedJobRoom('');
      setRoomProductDetails([]);
      setProductDetailStatusUpdates(new Map());
    }
  }, [selectedTaskType, editingTask]);

  // Filter out job-room options that already have assigned tasks
  const getAvailableJobRoomOptions = (): JobRoomOption[] => {
    if (editingTask) {
      // When editing, ensure the current task's job/room is always included
      // This allows editing DONE tasks even if they're filtered out for new tasks
      const options = [...jobRoomOptions];
      
      // Check if the editing task's job/room exists in the options
      if (editingTask.jobId && editingTask.roomId) {
        const existingOption = options.find(
          opt => opt.jobId === editingTask.jobId && opt.roomId === editingTask.roomId
        );

        // If not found, create a temporary option from the task data
        if (!existingOption) {
          options.push({
            jobId: editingTask.jobId,
            jobNumber: editingTask.jobNumber || `Job #${editingTask.jobId}`,
            roomId: editingTask.roomId,
            roomName: editingTask.roomName || `Room #${editingTask.roomId}`,
            displayText: editingTask.title || `${editingTask.jobNumber || `Job #${editingTask.jobId}`} - ${editingTask.roomName || `Room #${editingTask.roomId}`}`,
          });
        }
      } else if (editingTask.jobId) {
        // Room-less task — ensure the job-only option is present
        const existingOption = options.find(
          opt => opt.jobId === editingTask.jobId && opt.roomId === null
        );
        if (!existingOption) {
          options.push({
            jobId: editingTask.jobId,
            jobNumber: editingTask.jobNumber || `Job #${editingTask.jobId}`,
            roomId: null,
            roomName: null,
            displayText: `${editingTask.jobNumber || `Job #${editingTask.jobId}`} — No rooms yet`,
          });
        }
      }
      
      return options;
    }
    
    // When adding new task, backend already returns only unassigned job-room combinations
    // So we can just return jobRoomOptions directly
    // If no task type is selected yet, show all options (though this shouldn't happen)
    if (!selectedTaskType) {
      return jobRoomOptions;
    }
    
    // Backend already filtered out assigned job-room combinations, so return as-is
    return jobRoomOptions;
  };

  const availableJobRoomOptions = getAvailableJobRoomOptions();

  // Handle product detail status change
  const handleProductDetailStatusChange = (_productId: number, detailId: number, newStatus: string) => {
    const updates = new Map(productDetailStatusUpdates);
    if (newStatus !== '') {
      updates.set(detailId, newStatus);
    } else {
      updates.delete(detailId);
    }
    setProductDetailStatusUpdates(updates);
  };

  // Handle saving task
  const handleSave = async () => {
    if (!selectedJobRoom) {
      alert('Please select a job and room');
      return;
    }

    const dashIdx = selectedJobRoom.indexOf('-');
    const jobId = parseInt(selectedJobRoom.substring(0, dashIdx), 10);
    const roomIdStr = selectedJobRoom.substring(dashIdx + 1);
    const roomId: number | null = roomIdStr === 'null' ? null : parseInt(roomIdStr, 10);

    // First check availableJobRoomOptions (what's shown in dropdown)
    let selectedOption = availableJobRoomOptions.find(
      opt => opt.jobId === jobId && opt.roomId === roomId
    );

    // If not found, check jobRoomOptions
    if (!selectedOption) {
      selectedOption = jobRoomOptions.find(
        opt => opt.jobId === jobId && opt.roomId === roomId
      );
    }

    // If still not found and we're editing, use task data
    if (!selectedOption && editingTask && editingTask.jobId === jobId && editingTask.roomId === roomId) {
      selectedOption = {
        jobId: editingTask.jobId,
        jobNumber: editingTask.jobNumber || `Job #${editingTask.jobId}`,
        roomId: editingTask.roomId ?? null,
        roomName: editingTask.roomName || null,
        displayText: editingTask.title || `${editingTask.jobNumber || `Job #${editingTask.jobId}`} - ${editingTask.roomName || `Room #${editingTask.jobId}`}`,
      };
    }
    
    if (!selectedOption) {
      alert('Invalid job and room selection');
      return;
    }

    // Get team leader and member names
    const teamLeader = selectedTeamLeader ? users.find(u => u.id === selectedTeamLeader) : null;
    const members = users.filter(u => selectedMembers.includes(u.id));

    // Convert product detail status updates Map to object for storage
    const productDetailStatusUpdatesObj: Record<number, string> = {};
    productDetailStatusUpdates.forEach((status, detailId) => {
      productDetailStatusUpdatesObj[detailId] = status;
    });

    const taskData: any = {
      title: selectedOption.displayText,
      description: taskDescription,
      jobId: selectedOption.jobId,
      roomId: selectedOption.roomId,
      jobNumber: selectedOption.jobNumber,
      roomName: selectedOption.roomName,
      taskType: selectedTaskType || null,
      teamLeaderId: selectedTeamLeader ? Number(selectedTeamLeader) : null,
      teamLeaderName: teamLeader?.username || null,
      memberIds: selectedMembers.length > 0 ? selectedMembers : null,
      memberNames: members.length > 0 ? members.map(m => m.username) : null,
      productDetailStatusUpdates: Object.keys(productDetailStatusUpdatesObj).length > 0 ? productDetailStatusUpdatesObj : null,
    };
    
    // Set date fields based on multi-day selection
    if (isMultiDay && taskDate && endDate) {
      taskData.taskDate = taskDate;
      taskData.endDate = endDate;
    } else if (taskDate) {
      taskData.taskDate = taskDate;
      taskData.endDate = null;
    }

    // Add work location data to taskData
    taskData.workLocation = {
      hasWorkLocation,
      address: workLocationAddress,
      latitude: workLocationLatitude,
      longitude: workLocationLongitude,
      workLocationId: workLocationId,
      currentUserId,
      taskDate: taskDate || formatDateLocal(new Date()),
    };

    try {
      await onSave(taskData);
      handleClose();
    } catch (error) {
      console.error('Error saving task:', error);
      alert('Failed to save task. Please try again.');
    }
  };

  // Handle closing modal
  const handleClose = () => {
    setSelectedJobRoom('');
    setTaskDescription('');
    setTaskDate('');
    setEndDate('');
    setIsMultiDay(false);
    setSelectedTeamLeader('');
    setSelectedMembers([]);
    setSelectedTaskType('');
    setRoomProductDetails([]);
    setProductDetailStatusUpdates(new Map());
    setHasWorkLocation(false);
    setWorkLocationAddress('');
    setWorkLocationLatitude(null);
    setWorkLocationLongitude(null);
    setWorkLocationId(null);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editingTask ? 'Edit Task' : 'Add New Task'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {/* Task Type Radio Button Group */}
            <FormControl component="fieldset" disabled={!!editingTask}>
              <FormLabel component="legend">Task Type</FormLabel>
              <RadioGroup
                row
                value={selectedTaskType}
                onChange={(e) => setSelectedTaskType(e.target.value as 'Measure' | 'Shop Drawing' | 'Fabrication' | 'Install' | '')}
              >
                <FormControlLabel value="Measure" control={<Radio />} label="Measure" />
                <FormControlLabel value="Shop Drawing" control={<Radio />} label="Shop Drawing" />
                <FormControlLabel value="Fabrication" control={<Radio />} label="Fabrication" />
                <FormControlLabel value="Install" control={<Radio />} label="Install" />
              </RadioGroup>
            </FormControl>

            {loadingJobs ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress />
              </Box>
            ) : (
            <FormControl fullWidth required disabled={!!editingTask}>
                <InputLabel>Job and Room</InputLabel>
              <Select
                  value={selectedJobRoom}
                  onChange={(e) => setSelectedJobRoom(e.target.value)}
                  label="Job and Room"
                autoFocus={!editingTask}
              >
                  {availableJobRoomOptions.length === 0 ? (
                    <MenuItem value="" disabled>
                      {editingTask 
                        ? 'No rooms available (only jobs with status "Fabrication" and rooms with status "active" are shown)'
                        : 'No available rooms (all rooms already have assigned tasks or no rooms match the criteria)'}
                    </MenuItem>
                ) : (
                    availableJobRoomOptions.map((option) => (
                      <MenuItem key={`${option.jobId}-${option.roomId ?? 'null'}`} value={`${option.jobId}-${option.roomId ?? 'null'}`}>
                        {option.displayText}
                      </MenuItem>
                    ))
                )}
              </Select>
            </FormControl>
            )}

            {/* Notice for Measure task type */}
            {(selectedTaskType === 'Measure' || selectedTaskType === 'Shop Drawing') && (
              <Box sx={{ mt: 1, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Info color="primary" sx={{ fontSize: 20 }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                  Please submit results as photos and the pdf documents
                </Typography>
              </Box>
            )}

            {/* Product Details List - Show when room is selected and task type is not Measure */}
            {selectedJobRoom && selectedTaskType !== 'Measure' && selectedTaskType !== 'Shop Drawing' && (
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Product Details in Room
                </Typography>
                {loadingProducts ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : roomProductDetails.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No product details found in this room.
                  </Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Detail Name</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Current Status</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Completed Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {roomProductDetails.map(({ detail, product }) => {
                          const currentStatus = detail.status || 'N/A';
                          const updatedStatus = productDetailStatusUpdates.get(detail.id);
                          const detailName = getProductDetailName(product, detail);
                          return (
                            <TableRow key={detail.id}>
                              <TableCell>{product.product_name}</TableCell>
                              <TableCell>{detailName}</TableCell>
                              <TableCell>
                                <Chip
                                  label={currentStatus}
                                  size="small"
                                  color={currentStatus === 'FINISHED' || currentStatus === 'Installed' ? 'success' : 'default'}
                                />
                              </TableCell>
                              <TableCell>
                                <FormControl size="small" fullWidth>
                                  <Select
                                    value={updatedStatus !== undefined ? updatedStatus : ''}
                                    onChange={(e) => {
                                      const newStatus = e.target.value;
                                      handleProductDetailStatusChange(product.id, detail.id, newStatus);
                                    }}
                                    displayEmpty
                                  >
                                    <MenuItem value="">
                                      <em>No change</em>
                                    </MenuItem>
                                    {detailStatusOptions.map((status) => (
                                      <MenuItem key={status} value={status}>
                                        {status}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                                {updatedStatus && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                                    (Task progress only)
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}

            <TextField
              label="Description"
              value={taskDescription}
              onChange={(e) => setTaskDescription(e.target.value)}
              fullWidth
              multiline
              rows={4}
            />

            {/* Multi-day Task Checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={isMultiDay}
                  onChange={(e) => {
                    setIsMultiDay(e.target.checked);
                    if (e.target.checked) {
                      setEndDate(taskDate || formatDateLocal(new Date()));
                    } else {
                      setEndDate('');
                    }
                  }}
                />
              }
              label="Multi-day task"
            />

            {/* Task Date - Single Date */}
            {!isMultiDay && (
              <TextField
                label="Task Date"
                type="date"
                value={taskDate}
                onChange={(e) => {
                  setTaskDate(e.target.value);
                  if (!isMultiDay) {
                    setEndDate(e.target.value);
                  }
                }}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            )}

            {/* Date Range - Start and End Date */}
            {isMultiDay && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={taskDate}
                  onChange={(e) => setTaskDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Box>
            )}

            {/* Team Leader Selection */}
            <FormControl fullWidth>
              <InputLabel>Team Leader</InputLabel>
              <Select
                value={selectedTeamLeader}
                onChange={(e) => setSelectedTeamLeader(e.target.value as number | '')}
                label="Team Leader"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.username} {user.role ? `(${user.role})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Team Members Selection */}
            <FormControl fullWidth>
              <InputLabel>Team Members</InputLabel>
              <Select
                multiple
                value={selectedMembers}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedMembers(typeof value === 'string' ? [] : value);
                }}
                input={<OutlinedInput label="Team Members" />}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return <em>None</em>;
                  }
                  return selected
                    .map((id) => {
                      const user = users.find((u) => u.id === id);
                      return user?.username || '';
                    })
                    .filter(Boolean)
                    .join(', ');
                }}
              >
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.username} {user.role ? `(${user.role})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Work Location Section */}
            <Divider sx={{ my: 2 }} />
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasWorkLocation}
                  onChange={(e) => {
                    setHasWorkLocation(e.target.checked);
                    if (!e.target.checked) {
                      setWorkLocationAddress('');
                    }
                  }}
                />
              }
              label="Specify work location"
            />
            
            {hasWorkLocation && (
              <TextField
                label="Work Location Address"
                value={workLocationAddress}
                onClick={() => setOpenAddressMapModal(true)}
                onFocus={(e) => {
                  e.target.blur();
                  setOpenAddressMapModal(true);
                }}
                fullWidth
                placeholder="Click to select address on map"
                InputProps={{
                  readOnly: true,
                }}
              />
            )}

            {editingTask && (
              <Typography variant="body2" color="text.secondary">
                Note: New tasks can only be added to the TO DO column. You can move tasks between columns after creation.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={loadingJobs || !selectedJobRoom}>
            {editingTask ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Address Map Modal */}
      <AddressMapModal
        open={openAddressMapModal}
        onClose={() => setOpenAddressMapModal(false)}
        onConfirm={(address, placeDetails) => {
          setWorkLocationAddress(address);
          // Extract coordinates from placeDetails if available
          if (placeDetails && placeDetails.geometry && placeDetails.geometry.location) {
            const location = placeDetails.geometry.location;
            const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
            const lng = typeof location.lng === 'function' ? location.lng() : location.lng;
            setWorkLocationLatitude(Number(lat));
            setWorkLocationLongitude(Number(lng));
          } else {
            setWorkLocationLatitude(null);
            setWorkLocationLongitude(null);
          }
          setOpenAddressMapModal(false);
        }}
        initialAddress={workLocationAddress}
      />
    </>
  );
}
